import React, { useState, useEffect, useRef } from 'react';
import './TeamActivitiesModal.css';

const TeamActivitiesModal = ({ isOpen, onClose, user }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setUnseenCount] = useState(0);
  const [filter, setFilter] = useState('24h');
  const [visibleCount, setVisibleCount] = useState(20);
  const [activeTab, setActiveTab] = useState('all');
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchActivities();
    }
  }, [isOpen, filter, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      
      // Build filter parameter based on active tab
      let filterParam = filter;
      if (activeTab === 'my-team') {
        filterParam = `${filter}&team_only=true`;
      } else if (activeTab === 'recent') {
        filterParam = '30d';
      }
      
      const response = await fetch(`/backend/api/dashboard/team-activities.php?filter=${filterParam}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setActivities(data.activities || []);
        setUnseenCount(data.unseen_count || 0);
        setVisibleCount(20); // Reset visible count on new fetch
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsSeen = async () => {
    try {
      const response = await fetch('/backend/api/dashboard/mark-activities-seen.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ mark_all: true })
      });

      if (response.ok) {
        fetchActivities();
      }
    } catch (error) {
      console.error('Error marking activities as seen:', error);
    }
  };

  const markActivityAsSeen = async (activityId, activityType) => {
    try {
      const response = await fetch('/backend/api/dashboard/mark-activities-seen.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          activity_id: activityId, 
          activity_type: activityType 
        })
      });

      if (response.ok) {
        setActivities(prevActivities => 
          prevActivities.map(activity => 
            activity.activity_id === activityId 
              ? { ...activity, is_seen: true }
              : activity
          )
        );
        setUnseenCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking activity as seen:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getActivityIcon = (activity) => {
    // Use provided activity icons - new for unseen, seen for seen
    return activity.is_seen 
      ? '/assets/contacts/activities/seen.svg'
      : '/assets/contacts/activities/new.svg';
  };

  if (!isOpen) return null;

  return (
    <div className="team-activities-modal-overlay">
      <div className="team-activities-modal" ref={modalRef}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">Team Activities</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button 
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Activities
          </button>
          <button 
            className={`tab ${activeTab === 'my-team' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-team')}
          >
            My Team
          </button>
          <button 
            className={`tab ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
          >
            Recent
          </button>
        </div>

        {/* Filter Controls */}
        <div className="filter-controls">
          <div className="filter-buttons">
            <button 
              className={filter === 'today' ? 'active' : ''} 
              onClick={() => setFilter('today')}
            >
              Today
            </button>
            <button 
              className={filter === '24h' ? 'active' : ''} 
              onClick={() => setFilter('24h')}
            >
              24h
            </button>
            <button 
              className={filter === '3d' ? 'active' : ''} 
              onClick={() => setFilter('3d')}
            >
              3d
            </button>
            <button 
              className={filter === '7d' ? 'active' : ''} 
              onClick={() => setFilter('7d')}
            >
              7d
            </button>
            <button 
              className={filter === '30d' ? 'active' : ''} 
              onClick={() => setFilter('30d')}
            >
              30d
            </button>
          </div>
          
          {activities.length > 0 && (
            <button 
              className="mark-all-seen-btn"
              onClick={markAllAsSeen}
            >
              Mark all as seen
            </button>
          )}
        </div>

        {/* Content */}
        <div className="modal-content">
          {loading ? (
            <div className="loading-spinner">Loading activities...</div>
          ) : activities.length > 0 ? (
            <div className="activities-feed">
              {activities.slice(0, visibleCount).map((activity, index) => (
                <div 
                  key={index} 
                  className={`activity-card ${activity.is_seen ? 'seen' : 'unseen'}`}
                  onClick={() => !activity.is_seen && markActivityAsSeen(activity.activity_id, activity.type)}
                >
                  <div className="activity-avatar">
                    <img 
                      src={`/assets/Profile/${activity.user_avatar || 'boy-1.svg'}`}
                      alt={activity.user_name}
                      onError={(e) => {
                        e.target.src = '/assets/Profile/boy-1.svg';
                      }}
                    />
                  </div>
                  
                  <div className="activity-main">
                    <div className="activity-header">
                      <span className="activity-user">{activity.user_name}</span>
                      <img 
                        src={getActivityIcon(activity)} 
                        alt="" 
                        className="activity-icon"
                      />
                    </div>
                    <div className="activity-description">
                      {activity.action} {activity.target_name}
                    </div>
                  </div>
                  
                  <div className="activity-meta">
                    <span className="activity-time">{formatTimeAgo(activity.timestamp)}</span>
                    {!activity.is_seen && (
                      <div className="unseen-indicator"></div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Load More Button */}
              {activities.length > visibleCount && (
                <button 
                  className="load-more-btn" 
                  onClick={() => setVisibleCount(v => v + 20)}
                >
                  Load more...
                </button>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <p>No activities found</p>
              <p className="empty-subtitle">Team activity will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamActivitiesModal;
